"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

const links = [
  { href: "#what-we-do", label: "What we do" },
  { href: "#team", label: "Team" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const firstMobileLinkRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    firstMobileLinkRef.current?.focus();

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const root = document.documentElement;
    const onScroll = () => {
      root.classList.toggle("navbar-compact", window.scrollY > 10);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass =
    "relative -mx-2 -my-1 px-2 py-1 rounded-md transition-base hover:bg-gray-100 after:content-[''] after:absolute after:left-2 after:right-2 after:-bottom-0.5 after:h-0.5 after:bg-black after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform";

  return (
    <header className="navbar-fixed sticky top-0 z-40 w-full border-b border-black/10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 transition-[padding,box-shadow] duration-200">
      <div className="navbar-inner mx-auto max-w-6xl px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3" aria-label="Factor Omega - Return to top">
            <Image src="/icon_new.svg" alt="" width={32} height={32} className="h-8 w-8" priority />
            <span className="text-base font-semibold tracking-tight text-foreground">Factor Omega</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-6 text-sm text-foreground md:flex" aria-label="Primary">
            {links.map((link) => (
              <a key={link.href} className={linkClass} href={link.href}>
                {link.label}
              </a>
            ))}
            <a
              className="pressable inline-flex items-center justify-center rounded-full border border-black px-4 py-2 font-medium text-black transition-base hover:bg-black hover:text-white"
              href="#contact"
            >
              Get in touch
            </a>
          </nav>

          {/* Mobile Hamburger Button */}
          <button
            type="button"
            className="flex flex-col gap-1 p-3 md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            <span aria-hidden="true" className={`h-0.5 w-5 bg-black transition-all ${isMenuOpen ? "translate-y-1.5 rotate-45" : ""}`}></span>
            <span aria-hidden="true" className={`h-0.5 w-5 bg-black transition-all ${isMenuOpen ? "opacity-0" : ""}`}></span>
            <span aria-hidden="true" className={`h-0.5 w-5 bg-black transition-all ${isMenuOpen ? "-translate-y-1.5 -rotate-45" : ""}`}></span>
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <nav id="mobile-nav" className="mt-4 border-t border-black/10 pb-4 pt-4 md:hidden" aria-label="Mobile primary">
            <div className="flex flex-col gap-4 text-sm text-foreground">
              {links.map((link, idx) => (
                <a
                  key={link.href}
                  className="-mx-2 -my-1 rounded-md px-2 py-1 transition-base hover:bg-gray-100"
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  ref={idx === 0 ? firstMobileLinkRef : undefined}
                >
                  {link.label}
                </a>
              ))}
              <a
                className="pressable inline-flex w-fit items-center justify-center rounded-full border border-black px-4 py-2 font-medium text-black transition-base hover:bg-black hover:text-white"
                href="#contact"
                onClick={() => setIsMenuOpen(false)}
              >
                Get in touch
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
